import { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../auth/useAuth';
import { createDatafeed, ensureSymbol, findSymbol, getLoadedSymbols, loadSymbols, searchSymbols as searchChartSymbols } from '../chart/datafeed';
import { deleteAlarm, fetchAlarms } from '../lib/api/alarms';
import { chunkQuoteSymbols, createQuotesEventSource, fetchQuotes, quoteEventsMaxSymbols } from '../lib/api/quotes';
import { addWatchlistSection, addWatchlistSymbol, createWatchlist, createWatchlistEventSource, deleteWatchlist, deleteWatchlistItem, fetchWatchlists, importLocalWatchlist, importWatchlistFile, renameWatchlist, renameWatchlistItem, setWatchlistItemFlag, setWatchlistPinned, updateWatchlistOrder, updateWatchlistsOrder } from '../lib/api/watchlist';
import { publishQuoteUpdates } from '../lib/quotes/liveQuotes';
import { fetchUserSettings, saveUserSetting } from '../lib/api/settings';
import { loadStoredJSON, storeJSON } from '../settings/storage';
import { useChartPersistence } from '../settings/useChartPersistence';
import type { TradingViewDatafeed } from '../types/datafeed-api';
import type { ApiWatchlist, ChartSymbol, PriceAlarm, WatchlistFlagColor, WatchlistImportResponse } from '../types/api';
import type { JsonValue, UserSettings } from '../types/settings';
import { remoteChartStateKey } from '../types/settings';
import type { TradingViewChartHandle } from '../chart/TradingViewChart';
import { activeWatchlistStorageKey, chartStateStorageKey, errorMessage, getInitialChartState, loadStoredActiveWatchlistId, loadStoredChartState, moveWatchlist, moveWatchlistItem, normalizeWatchlists, quoteFromApi, shortSymbolName, toWatchlistViewItems, updateActiveWatchlistItems, watchlistHasSymbol, type StoredChartState } from './AppShell.utils';
import type { WatchlistCategory, WatchlistQuote, WatchlistSort, WatchlistViewItem } from './AppShell.types';
import { addLocalWatchlistSection, addLocalWatchlistSymbol, deleteLocalWatchlistItem, loadLocalWatchlist, loadStoredLocalWatchlist, renameLocalWatchlistItem, reorderLocalWatchlistItems, setLocalWatchlistItemFlag } from './localWatchlist';

const activeWatchlistRemoteSettingKey = 'active_watchlist_id';
const latestWatchlistFlagColorStorageKey = 'app.latestWatchlistFlagColor';
const defaultWatchlistFlagColor: WatchlistFlagColor = 'blue';

function loadStoredWatchlistFlagColor(): WatchlistFlagColor {
  const value = loadStoredJSON<string>(latestWatchlistFlagColorStorageKey, defaultWatchlistFlagColor);
  return isWatchlistFlagColor(value) ? value : defaultWatchlistFlagColor;
}

function isWatchlistFlagColor(value: string): value is WatchlistFlagColor {
  return value === 'red' || value === 'orange' || value === 'yellow' || value === 'green' || value === 'blue' || value === 'purple';
}

export function useAppData() {
  const { user, checkingSession, signOut } = useAuth();
  const [symbolsReady, setSymbolsReady] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [watchlistSort, setWatchlistSort] = useState<WatchlistSort>('manual');
  const [latestWatchlistFlagColor, setLatestWatchlistFlagColor] = useState<WatchlistFlagColor>(() => loadStoredWatchlistFlagColor());
  const [storedChartState] = useState(() => loadStoredChartState());
  const [remoteChartState, setRemoteChartState] = useState<StoredChartState | null>(null);
  const [watchlists, setWatchlists] = useState<ApiWatchlist[]>([]);
  const [activeWatchlistId, setActiveWatchlistId] = useState<string | null>(() => loadStoredActiveWatchlistId());
  const [selectedSymbol, setSelectedSymbol] = useState<string | null>(null);
  const [selectedInterval, setSelectedInterval] = useState<string | null>(null);
  const [watchlistLoading, setWatchlistLoading] = useState(true);
  const [watchlistsReady, setWatchlistsReady] = useState(false);
  const [quotesLoading, setQuotesLoading] = useState(false);
  const [symbolPickerOpen, setSymbolPickerOpen] = useState(false);
  const [symbolPickerSymbols, setSymbolPickerSymbols] = useState<ChartSymbol[]>([]);
  const [symbolPickerLoading, setSymbolPickerLoading] = useState(false);
  const [symbolPickerCategory, setSymbolPickerCategory] = useState<WatchlistCategory>('all');
  const [addToWatchlistSymbol, setAddToWatchlistSymbol] = useState<ChartSymbol | null>(null);
  const [addingWatchlistId, setAddingWatchlistId] = useState<string | null>(null);
  const [symbolSearch, setSymbolSearch] = useState('');
  const [draggingItemId, setDraggingItemId] = useState<string | null>(null);
  const [dragOverItemId, setDragOverItemId] = useState<string | null>(null);
  const [draggingWatchlistId, setDraggingWatchlistId] = useState<string | null>(null);
  const [dragOverWatchlistId, setDragOverWatchlistId] = useState<string | null>(null);
  const [alarmSymbol, setAlarmSymbol] = useState<ChartSymbol | null>(null);
  const [alarmInitialPrice, setAlarmInitialPrice] = useState<number | undefined>(undefined);
  const [draftAlarmPrice, setDraftAlarmPrice] = useState<number | null>(null);
  const [alarms, setAlarms] = useState<PriceAlarm[]>([]);
  const [watchlistError, setWatchlistError] = useState<string | null>(null);
  const [quotes, setQuotes] = useState<Record<string, WatchlistQuote | undefined>>({});
  const datafeedRef = useRef<TradingViewDatafeed | null>(null);
  const datafeedNineWeekRef = useRef<boolean | null>(null);
  const chartRef = useRef<TradingViewChartHandle | null>(null);
  const watchlistItemsRef = useRef<WatchlistViewItem[]>([]);
  const reorderStartRef = useRef<ApiWatchlist[] | null>(null);
  const watchlistsOrderRef = useRef<ApiWatchlist[]>([]);
  const watchlistsOrderStartRef = useRef<ApiWatchlist[] | null>(null);
  const watchlistMutationVersionRef = useRef(0);
  const watchlistMutationPendingRef = useRef(0);
  const activeWatchlistSyncTimerRef = useRef<number | null>(null);
  const syncedActiveWatchlistIdRef = useRef<string | null | undefined>(undefined);
  const attemptedWatchlistMigrationRef = useRef<Set<string>>(new Set());
  const syncedChartStateRef = useRef<StoredChartState | null>(null);
  const persistence = useChartPersistence(user);
  const symbols = Object.values(getLoadedSymbols());
  const activeWatchlist = watchlists.find((watchlist) => watchlist.id === activeWatchlistId) ?? watchlists[0] ?? null;
  const activeWatchlistItems = useMemo(() => toWatchlistViewItems(activeWatchlist), [activeWatchlist]);
  const activeWatchlistSymbols = useMemo(() => activeWatchlistItems.flatMap((item) => item.symbolData && !item.isInactive ? [item.symbolData] : []), [activeWatchlistItems]);
  const watchlistSymbolGroups = useMemo(() => watchlists.map((watchlist) => [...new Set(watchlist.items.flatMap((item) => {
    if (item.item_type !== 'symbol') return [];
    const symbolName = item.symbol_detail?.symbol ?? item.symbol ?? item.symbol_detail?.name;
    return symbolName ? [symbolName.trim().toUpperCase()] : [];
  }).filter(Boolean))]).filter((group) => group.length > 0), [watchlists]);
  const watchlistNamesKey = activeWatchlistSymbols.map((symbol) => symbol.name).join('|');
  const watchlistGroupsKey = watchlistSymbolGroups.map((group) => group.join(',')).join('|');
  const hasWatchlistQuotes = activeWatchlistSymbols.some((symbol) => Boolean(quotes[symbol.name]));
  const watchlistSymbolNames = useMemo(() => new Set(activeWatchlistSymbols.map((symbol) => symbol.name)), [watchlistNamesKey]);
  const flaggedWatchlistSymbolNames = useMemo(() => new Set(activeWatchlistItems.flatMap((item) => {
    if (item.item_type !== 'symbol' || !item.flag_color) return [];
    const symbolName = item.symbolData?.name ?? item.symbol ?? item.symbol_detail?.symbol ?? item.symbol_detail?.name;
    return symbolName ? [symbolName] : [];
  })), [activeWatchlistItems]);
  const flaggedWatchlistSymbolColors = useMemo(() => activeWatchlistItems.reduce<Record<string, WatchlistFlagColor>>((result, item) => {
    if (item.item_type !== 'symbol' || !item.flag_color) return result;
    const symbolName = item.symbolData?.name ?? item.symbol ?? item.symbol_detail?.symbol ?? item.symbol_detail?.name;
    if (symbolName) result[symbolName] = item.flag_color;
    return result;
  }, {}), [activeWatchlistItems]);
  const canUseNineWeekInterval = Boolean(user);
  const { symbol: initialSymbol, interval: initialInterval } = getInitialChartState(persistence.layout, Object.keys(getLoadedSymbols())[0] || 'BTCUSDT');
  const ready = symbolsReady && persistence.ready;
  const chartSymbol = selectedSymbol || remoteChartState?.symbol || storedChartState?.symbol || initialSymbol;
  const requestedChartInterval = selectedInterval || remoteChartState?.interval || storedChartState?.interval || initialInterval;
  const chartInterval = !canUseNineWeekInterval && requestedChartInterval === '9W' ? '1W' : requestedChartInterval;
  const chartSymbolData = findSymbol(chartSymbol) ?? activeWatchlistSymbols.find((symbol) => symbol.name === chartSymbol) ?? null;
  const chartAlarmSymbol = shortSymbolName(chartSymbol);
  const chartAlarms = useMemo(() => alarms.filter((alarm) => alarm.status === 'active' && alarm.symbol === chartAlarmSymbol), [alarms, chartAlarmSymbol]);

  if (!datafeedRef.current || datafeedNineWeekRef.current !== canUseNineWeekInterval) {
    datafeedRef.current = createDatafeed({ includeNineWeek: canUseNineWeekInterval });
    datafeedNineWeekRef.current = canUseNineWeekInterval;
  }

  watchlistItemsRef.current = activeWatchlistItems;
  watchlistsOrderRef.current = watchlists;

  useEffect(() => {
    let cancelled = false;
    setSymbolsReady(false);
    setWatchlistLoading(true);
    loadSymbols(true)
      .then(() => {
        if (!cancelled) setSymbolsReady(true);
      })
      .catch((err) => {
        console.error('loadSymbols:', err);
        if (!cancelled) setSymbolsReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!symbolsReady) return;
    if (!user) {
      const localWatchlist = loadLocalWatchlist(getLoadedSymbols());
      applyWatchlists([localWatchlist], localWatchlist.id);
      setWatchlistLoading(false);
      setWatchlistsReady(true);
      setWatchlistError(null);
      return;
    }
    let cancelled = false;
    const startedVersion = watchlistMutationVersionRef.current;
    setWatchlistLoading(true);
    setWatchlistsReady(false);
    Promise.all([
      fetchWatchlists(),
      fetchUserSettings().catch((err) => {
        console.error('fetchUserSettings:', err);
        return {} as UserSettings;
      }),
    ])
      .then(async ([rows, settings]) => {
        if (!cancelled && startedVersion === watchlistMutationVersionRef.current && watchlistMutationPendingRef.current === 0) {
          const remoteChartState = getRemoteChartState(settings[remoteChartStateKey]);
          if (remoteChartState) {
            setRemoteChartState(remoteChartState);
            syncedChartStateRef.current = remoteChartState;
          } else if (storedChartState) {
            const nextStoredChartState = { symbol: storedChartState.symbol, interval: storedChartState.interval };
            saveUserSetting(remoteChartStateKey, nextStoredChartState)
              .then(() => {
                syncedChartStateRef.current = storedChartState;
              })
              .catch((err) => {
                console.error(`saveUserSetting(${remoteChartStateKey}):`, err);
            });
            if (cancelled) return;
            setRemoteChartState(storedChartState);
          }

          let nextRows = rows;
          let preferredId = typeof settings[activeWatchlistRemoteSettingKey] === 'string' && settings[activeWatchlistRemoteSettingKey]
            ? settings[activeWatchlistRemoteSettingKey]
            : null;
          const migrationKey = user.id;
          const storedLocalWatchlist = loadStoredLocalWatchlist();
          if (rows.length === 0 && storedLocalWatchlist && !attemptedWatchlistMigrationRef.current.has(migrationKey)) {
            attemptedWatchlistMigrationRef.current.add(migrationKey);
            try {
              const migrated = await importLocalWatchlist(storedLocalWatchlist);
              if (cancelled) return;
              nextRows = migrated.watchlists;
              preferredId = migrated.createdWatchlistId;
              await saveUserSetting(activeWatchlistRemoteSettingKey, migrated.createdWatchlistId);
            } catch (err) {
              attemptedWatchlistMigrationRef.current.delete(migrationKey);
              console.error('importLocalWatchlist:', err);
              setWatchlistError(errorMessage(err));
            }
          }

          syncedActiveWatchlistIdRef.current = preferredId;
          applyWatchlists(nextRows, preferredId);
        }
      })
      .catch((err) => {
        console.error('fetchWatchlists:', err);
        if (!cancelled) setWatchlistError(errorMessage(err));
      })
      .finally(() => {
        if (!cancelled) {
          setWatchlistLoading(false);
          setWatchlistsReady(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [storedChartState, symbolsReady, user]);

  useEffect(() => {
    if (!user || !watchlistsReady) return;
    const nextChartState = { symbol: chartSymbol, interval: chartInterval };
    const synced = syncedChartStateRef.current;
    if (synced?.symbol === nextChartState.symbol && synced.interval === nextChartState.interval) return;
    saveUserSetting(remoteChartStateKey, nextChartState)
      .then(() => {
        syncedChartStateRef.current = nextChartState;
      })
      .catch((err) => {
        console.error(`saveUserSetting(${remoteChartStateKey}):`, err);
      });
  }, [chartInterval, chartSymbol, user, watchlistsReady]);

  useEffect(() => {
	if (!user || !watchlistsReady) return;
	if (syncedActiveWatchlistIdRef.current === undefined) return;
	if (activeWatchlistId === syncedActiveWatchlistIdRef.current) return;
	if (activeWatchlistSyncTimerRef.current !== null) {
		window.clearTimeout(activeWatchlistSyncTimerRef.current);
	}
	activeWatchlistSyncTimerRef.current = window.setTimeout(() => {
		activeWatchlistSyncTimerRef.current = null;
		saveUserSetting(activeWatchlistRemoteSettingKey, activeWatchlistId)
			.then(() => {
				syncedActiveWatchlistIdRef.current = activeWatchlistId;
			})
			.catch((err) => {
				console.error(`saveUserSetting(${activeWatchlistRemoteSettingKey}):`, err);
			});
	}, 300);

	return () => {
		if (activeWatchlistSyncTimerRef.current !== null) {
			window.clearTimeout(activeWatchlistSyncTimerRef.current);
			activeWatchlistSyncTimerRef.current = null;
		}
	};
  }, [activeWatchlistId, user, watchlistsReady]);

  useEffect(() => {
    if (!user || !symbolsReady || !watchlistsReady) return undefined;
    let cancelled = false;
    let refetching = false;
    const events = createWatchlistEventSource();

    async function refreshWatchlist() {
      if (cancelled || refetching || watchlistMutationPendingRef.current > 0) return;
      const startedVersion = watchlistMutationVersionRef.current;
      refetching = true;
      try {
        const rows = await fetchWatchlists();
        if (!cancelled && startedVersion === watchlistMutationVersionRef.current && watchlistMutationPendingRef.current === 0) {
          applyWatchlists(rows);
        }
      } catch (err) {
        console.error('watchlist events:', err);
      } finally {
        refetching = false;
      }
    }

    events.addEventListener('watchlist', () => {
      void refreshWatchlist();
    });
    const interval = window.setInterval(() => {
      if (document.visibilityState === 'visible') void refreshWatchlist();
    }, 15_000);
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') void refreshWatchlist();
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      cancelled = true;
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      events.close();
    };
  }, [symbolsReady, user, watchlistsReady]);

  useEffect(() => {
    if (!symbolsReady || !watchlistsReady) return undefined;

    let cancelled = false;
    let fallbackInterval: number | null = null;
    let prefetchTimeout: number | null = null;
    setQuotesLoading(true);

    const activeSymbols = activeWatchlistSymbols.map((symbol) => symbol.name);
    const targetSymbols = [...new Set(activeSymbols.map((symbol) => symbol.trim().toUpperCase()).filter(Boolean))];
    const targetSymbolSet = new Set(targetSymbols);
    const targetGroups = chunkQuoteSymbols(targetSymbols);
    const normalizedChartSymbol = chartSymbol.trim().toUpperCase();
    const chartSymbolGroup = normalizedChartSymbol && !targetSymbolSet.has(normalizedChartSymbol) ? [[normalizedChartSymbol]] : [];
    const prefetchGroups = chartSymbolGroup.concat(watchlistSymbolGroups
      .map((group) => group.filter((symbol) => !targetSymbolSet.has(symbol)))
      .filter((group) => group.length > 0)
      .flatMap((group) => chunkQuoteSymbols(group)));

    function mergeQuotes(entries: Awaited<ReturnType<typeof fetchQuotes>>) {
      publishQuoteUpdates(entries);
      setQuotes((current) => {
        const next = { ...current };
        for (const quote of entries) {
          next[quote.symbol] = quoteFromApi(quote);
        }
        return next;
      });
    }

    async function loadQuoteGroups(groups: string[][], markReady = false) {
      for (const group of groups) {
        if (cancelled || group.length === 0) continue;
        try {
          const entries = await fetchQuotes(group);
          if (!cancelled) mergeQuotes(entries);
        } catch (err) {
          console.error('watchlist quotes:', err);
        }
      }
      if (!cancelled && markReady) setQuotesLoading(false);
    }

    function startFallbackPolling() {
      if (fallbackInterval !== null) return;
      fallbackInterval = window.setInterval(() => void loadQuoteGroups(targetGroups), 5_000);
    }

    void loadQuoteGroups(targetGroups, true);
    if (prefetchGroups.length > 0 && targetSymbols.length <= quoteEventsMaxSymbols) {
      prefetchTimeout = window.setTimeout(() => void loadQuoteGroups(prefetchGroups), 800);
    }
    if (targetSymbols.length === 0 || typeof EventSource === 'undefined' || targetSymbols.length > quoteEventsMaxSymbols) {
      startFallbackPolling();
      return () => {
        cancelled = true;
        if (prefetchTimeout !== null) window.clearTimeout(prefetchTimeout);
        if (fallbackInterval !== null) window.clearInterval(fallbackInterval);
      };
    }

    const events = createQuotesEventSource(targetSymbols);
    events.addEventListener('quotes', (event) => {
      try {
        const entries = JSON.parse(event.data) as Awaited<ReturnType<typeof fetchQuotes>>;
        if (cancelled || !Array.isArray(entries)) return;
        if (fallbackInterval !== null) {
          window.clearInterval(fallbackInterval);
          fallbackInterval = null;
        }
        mergeQuotes(entries);
        setQuotesLoading(false);
      } catch (err) {
        console.error('watchlist quote events:', err);
        startFallbackPolling();
      }
    });
    events.addEventListener('error', () => {
      startFallbackPolling();
    });

    return () => {
      cancelled = true;
      events.close();
      if (prefetchTimeout !== null) window.clearTimeout(prefetchTimeout);
      if (fallbackInterval !== null) window.clearInterval(fallbackInterval);
    };
  }, [chartSymbol, symbolsReady, watchlistGroupsKey, watchlistNamesKey, watchlistsReady]);

  useEffect(() => {
    storeJSON(chartStateStorageKey, { symbol: chartSymbol, interval: chartInterval });
  }, [chartInterval, chartSymbol]);

  useEffect(() => {
	storeJSON(activeWatchlistStorageKey, activeWatchlistId);
  }, [activeWatchlistId]);

  useEffect(() => {
    if (!user) return undefined;
    let cancelled = false;
    let refetching = false;

    async function refreshAlarms() {
      if (cancelled || refetching) return;
      refetching = true;
      try {
        const nextAlarms = await fetchAlarms();
        if (!cancelled) setAlarms(nextAlarms);
      } catch (err) {
        console.error('fetchAlarms:', err);
      } finally {
        refetching = false;
      }
    }

    void refreshAlarms();
    const interval = window.setInterval(() => {
      if (document.visibilityState === 'visible') void refreshAlarms();
    }, 15_000);
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') void refreshAlarms();
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [user]);

  useEffect(() => {
    if (!symbolPickerOpen) return undefined;
    let cancelled = false;
    const timeout = window.setTimeout(() => {
      setSymbolPickerLoading(true);
      searchChartSymbols(symbolSearch, {
        type: symbolPickerCategory === 'all' ? undefined : symbolPickerCategory,
        limit: 60,
      })
        .then((items) => {
          if (!cancelled) setSymbolPickerSymbols(items);
        })
        .catch((err) => {
          console.error('searchSymbols:', err);
          if (!cancelled) setSymbolPickerSymbols([]);
        })
        .finally(() => {
          if (!cancelled) setSymbolPickerLoading(false);
        });
    }, symbolSearch.trim() ? 180 : 0);
    return () => {
      cancelled = true;
      window.clearTimeout(timeout);
    };
  }, [symbolPickerCategory, symbolPickerOpen, symbolSearch]);

  async function syncNow() {
    if (!chartRef.current) return;
    setSyncing(true);
    try {
      const layout = await chartRef.current.saveLayout();
      await persistence.syncNow(layout);
    } finally {
      setSyncing(false);
    }
  }

  function beginWatchlistMutation(): () => void {
    watchlistMutationVersionRef.current += 1;
    watchlistMutationPendingRef.current += 1;
    return () => {
      watchlistMutationPendingRef.current = Math.max(0, watchlistMutationPendingRef.current - 1);
      watchlistMutationVersionRef.current += 1;
    };
  }

  function applyWatchlists(nextWatchlists: ApiWatchlist[], preferredId?: string | null) {
    const normalizedWatchlists = normalizeWatchlists(nextWatchlists);
    setWatchlists(normalizedWatchlists);
    setActiveWatchlistId((current) => {
      const targetId = preferredId ?? current;
      if (targetId && normalizedWatchlists.some((watchlist) => watchlist.id === targetId)) return targetId;
      return normalizedWatchlists[0]?.id ?? null;
    });
  }

  function requireActiveWatchlist(): ApiWatchlist | null {
    if (activeWatchlist) return activeWatchlist;
    setWatchlistError('Create a watchlist first');
    return null;
  }

  function requestAddSymbolToWatchlist(symbol: ChartSymbol) {
    if (watchlists.length === 0) {
      setWatchlistError('Create a watchlist first');
      return;
    }
    setWatchlistError(null);
    setSymbolPickerOpen(false);
    setAddToWatchlistSymbol(symbol);
  }

  async function addSymbolToWatchlist(symbol: ChartSymbol, options: { closePicker?: boolean; watchlistId?: string | null } = {}): Promise<boolean> {
    const watchlist = options.watchlistId
      ? watchlists.find((candidate) => candidate.id === options.watchlistId) ?? null
      : activeWatchlist;
    if (!watchlist) {
      setWatchlistError('Create a watchlist first');
      return false;
    }
    const closePicker = options.closePicker ?? true;
    setWatchlistError(null);
    if (!user) {
      const next = addLocalWatchlistSymbol(watchlist, symbol);
      applyWatchlists([next], next.id);
      if (closePicker) {
        setSymbolSearch('');
        setSymbolPickerOpen(false);
      }
      return true;
    }
    const finishMutation = beginWatchlistMutation();
    try {
      const rows = await addWatchlistSymbol(watchlist.id, symbol.name);
      applyWatchlists(rows, watchlist.id);
      if (closePicker) {
        setSymbolSearch('');
        setSymbolPickerOpen(false);
      }
      return true;
    } catch (err) {
      console.error('addWatchlistSymbol:', err);
      setWatchlistError(errorMessage(err));
      return false;
    } finally {
      finishMutation();
    }
  }

  async function addPendingSymbolToWatchlist(watchlist: ApiWatchlist) {
    if (!addToWatchlistSymbol || addingWatchlistId) return;
    setAddingWatchlistId(watchlist.id);
    try {
      const added = await addSymbolToWatchlist(addToWatchlistSymbol, { watchlistId: watchlist.id });
      if (added) {
        setAddToWatchlistSymbol(null);
        setSymbolPickerOpen(false);
      }
    } finally {
      setAddingWatchlistId(null);
    }
  }

  async function handleAddToWatchlistFromChart(symbolName: string) {
    const resolved = findSymbol(symbolName);
    if (resolved) {
      requestAddSymbolToWatchlist(resolved);
      return;
    }
    setWatchlistError(null);
    try {
      const found = await ensureSymbol(symbolName);
      if (found) {
        requestAddSymbolToWatchlist(found);
        return;
      }
      setWatchlistError(`Symbol ${symbolName} not found`);
    } catch (err) {
      console.error('ensureSymbol:', err);
      setWatchlistError(errorMessage(err));
    }
  }

  async function handleRemoveFromWatchlistFromChart(symbolName: string) {
    const resolved = findSymbol(symbolName);
    if (resolved) {
      void removeSymbolFromWatchlist(resolved);
      return;
    }

    setWatchlistError(null);
    try {
      const found = await ensureSymbol(symbolName);
      if (found) {
        void removeSymbolFromWatchlist(found);
        return;
      }
      setWatchlistError(`Symbol ${symbolName} not found`);
    } catch (err) {
      console.error('ensureSymbol:', err);
      setWatchlistError(errorMessage(err));
    }
  }

  async function toggleCurrentChartSymbolInWatchlist(symbolName = chartSymbol): Promise<boolean> {
	const watchlist = requireActiveWatchlist();
	if (!watchlist) return false;

	const targetName = symbolName;
	setWatchlistError(null);
	try {
	  const resolved = findSymbol(targetName) ?? await ensureSymbol(targetName);
	  if (!resolved) {
		setWatchlistError(`Symbol ${targetName} not found`);
		return false;
	  }

	  if (watchlistHasSymbol(watchlist, resolved)) {
		return await removeSymbolFromWatchlist(resolved);
	  }

	  return await addSymbolToWatchlist(resolved, { closePicker: false });
	} catch (err) {
	  console.error('toggleCurrentChartSymbolInWatchlist:', err);
	  setWatchlistError(errorMessage(err));
	  return false;
	}
  }

  async function toggleCurrentChartSymbolFlag(symbolName = chartSymbol): Promise<boolean> {
	return await setCurrentChartSymbolFlag(symbolName, undefined);
  }

  async function setCurrentChartSymbolFlag(symbolName = chartSymbol, flagColor: WatchlistFlagColor | null | undefined): Promise<boolean> {
	const watchlist = requireActiveWatchlist();
	if (!watchlist) return false;

	const targetName = symbolName;
	setWatchlistError(null);
	try {
	  const resolved = findSymbol(targetName) ?? await ensureSymbol(targetName);
	  if (!resolved) {
		setWatchlistError(`Symbol ${targetName} not found`);
		return false;
	  }

	  const item = findActiveWatchlistItemForSymbol(resolved);
	  if (!item) {
		setWatchlistError(`Symbol ${targetName} is not in the active watchlist`);
		return false;
	  }

	  const nextFlagColor = flagColor === undefined ? (item.flag_color ? null : latestWatchlistFlagColor) : flagColor;
	  await setWatchlistItemFlagColor(item, nextFlagColor);
	  return true;
	} catch (err) {
	  console.error('setCurrentChartSymbolFlag:', err);
	  setWatchlistError(errorMessage(err));
	  return false;
	}
  }

  async function removeSymbolFromWatchlist(symbol: ChartSymbol): Promise<boolean> {
    const item = findActiveWatchlistItemForSymbol(symbol);
    if (!item) return false;
    return await removeWatchlistItem(item);
  }

  function findActiveWatchlistItemForSymbol(symbol: ChartSymbol): WatchlistViewItem | undefined {
	return activeWatchlistItems.find((candidate) => candidate.item_type === 'symbol'
	  && (candidate.symbolData?.name === symbol.name
		|| candidate.symbol === symbol.name
		|| candidate.symbol_detail?.symbol === symbol.name
		|| candidate.symbol_detail?.name === symbol.name));
  }

  async function removeWatchlistItem(item: WatchlistViewItem): Promise<boolean> {
    setWatchlistError(null);
    const previous = watchlists;
    setWatchlists((current) => updateActiveWatchlistItems(current, activeWatchlist?.id ?? '', (items) => items.filter((candidate) => candidate.id !== item.id)));
    if (!user && activeWatchlist) {
      const next = deleteLocalWatchlistItem(activeWatchlist, item.id);
      applyWatchlists([next], next.id);
      return true;
    }
    const finishMutation = beginWatchlistMutation();
    try {
      const rows = await deleteWatchlistItem(item.id);
      applyWatchlists(rows, activeWatchlist?.id);
      return true;
    } catch (err) {
      console.error('deleteWatchlistItem:', err);
      setWatchlists(previous);
      setWatchlistError(errorMessage(err));
      return false;
    } finally {
      finishMutation();
    }
  }

  async function addSectionToWatchlist(name: string) {
    const watchlist = requireActiveWatchlist();
    if (!watchlist) return;
    if (!name?.trim()) return;
    setWatchlistError(null);
    if (!user) {
      const next = addLocalWatchlistSection(watchlist, name);
      applyWatchlists([next], next.id);
      return;
    }
    const finishMutation = beginWatchlistMutation();
    try {
      const rows = await addWatchlistSection(watchlist.id, name);
      applyWatchlists(rows, watchlist.id);
    } catch (err) {
      console.error('addWatchlistSection:', err);
      setWatchlistError(errorMessage(err));
    } finally {
      finishMutation();
    }
  }

  async function addSectionAboveWatchlistItem(targetItem: WatchlistViewItem, name: string) {
    const watchlist = requireActiveWatchlist();
    if (!watchlist) return;
    if (!name?.trim()) return;

    setWatchlistError(null);
    setWatchlistSort('manual');
    if (!user) {
      const targetIndex = watchlist.items.findIndex((item) => item.id === targetItem.id);
      const next = addLocalWatchlistSection(watchlist, name, targetIndex < 0 ? watchlist.items.length : targetIndex);
      applyWatchlists([next], next.id);
      return;
    }
    const finishMutation = beginWatchlistMutation();
    try {
      const previousItemIds = new Set(watchlist.items.map((item) => item.id));
      const rows = await addWatchlistSection(watchlist.id, name);
      const updatedWatchlist = rows.find((item) => item.id === watchlist.id);
      const createdSection = updatedWatchlist?.items.find((item) => item.item_type === 'section' && !previousItemIds.has(item.id));
      if (!updatedWatchlist || !createdSection) {
        applyWatchlists(rows, watchlist.id);
        return;
      }

      const itemsWithoutCreated = updatedWatchlist.items.filter((item) => item.id !== createdSection.id);
      const targetIndex = itemsWithoutCreated.findIndex((item) => item.id === targetItem.id);
      if (targetIndex < 0) {
        applyWatchlists(rows, watchlist.id);
        return;
      }

      const nextItems = [...itemsWithoutCreated];
      nextItems.splice(targetIndex, 0, createdSection);
      const orderedRows = await updateWatchlistOrder(watchlist.id, nextItems.map((item) => item.id));
      applyWatchlists(orderedRows, watchlist.id);
    } catch (err) {
      console.error('addSectionAboveWatchlistItem:', err);
      setWatchlistError(errorMessage(err));
    } finally {
      finishMutation();
    }
  }

  async function createNewWatchlist(name: string) {
    if (!name?.trim()) return;
    if (!user) {
      setWatchlistError('Sign up to create more watchlists');
      return;
    }
    setWatchlistError(null);
    const finishMutation = beginWatchlistMutation();
    try {
      const rows = await createWatchlist(name);
      const created = rows.find((watchlist) => watchlist.name === name.trim()) ?? rows[rows.length - 1];
      applyWatchlists(rows, created?.id);
    } catch (err) {
      console.error('createWatchlist:', err);
      setWatchlistError(errorMessage(err));
    } finally {
      finishMutation();
    }
  }

  async function renameWatchlistWithName(watchlist: ApiWatchlist, name: string) {
	if (!name?.trim() || name.trim() === watchlist.name) return;
	if (!user) {
	  setWatchlistError('Sign up to rename watchlists');
	  return;
	}
	setWatchlistError(null);
	const finishMutation = beginWatchlistMutation();
	try {
	  const rows = await renameWatchlist(watchlist.id, name);
	  applyWatchlists(rows, watchlist.id);
	} catch (err) {
	  console.error('renameWatchlist:', err);
	  setWatchlistError(errorMessage(err));
	} finally {
	  finishMutation();
	}
  }

  async function toggleWatchlistPinned(watchlist: ApiWatchlist) {
	if (!user) {
	  setWatchlistError('Sign up to pin watchlists');
	  return;
	}
	setWatchlistError(null);
	const nextPinned = !watchlist.is_pinned;
	const previous = watchlists;
	setWatchlists((current) => current.map((item) => item.id === watchlist.id ? { ...item, is_pinned: nextPinned } : item));
	const finishMutation = beginWatchlistMutation();
	try {
	  const rows = await setWatchlistPinned(watchlist.id, nextPinned);
	  applyWatchlists(rows, activeWatchlistId);
	} catch (err) {
	  console.error('setWatchlistPinned:', err);
	  setWatchlists(previous);
	  setWatchlistError(errorMessage(err));
	} finally {
	  finishMutation();
	}
  }

  async function importFileToWatchlist(file: File): Promise<WatchlistImportResponse | null> {
    if (!user) {
      setWatchlistError('Sign up to import watchlists');
      return null;
    }
    setWatchlistError(null);
    const finishMutation = beginWatchlistMutation();
    try {
      const result = await importWatchlistFile(file);
      applyWatchlists(result.watchlists, result.created_watchlist_id);
      return result;
    } catch (err) {
      console.error('importWatchlistFile:', err);
      setWatchlistError(errorMessage(err));
      return null;
    } finally {
      finishMutation();
    }
  }

  async function deleteWatchlistDirect(watchlist: ApiWatchlist) {
	if (!user) {
	  setWatchlistError('Sign up to delete watchlists');
	  return;
	}
	setWatchlistError(null);
	const finishMutation = beginWatchlistMutation();
	try {
	  const rows = await deleteWatchlist(watchlist.id);
	  applyWatchlists(rows);
	} catch (err) {
	  console.error('deleteWatchlist:', err);
	  setWatchlistError(errorMessage(err));
	} finally {
	  finishMutation();
	}
  }
  async function renameSectionWithName(item: WatchlistViewItem, name: string) {
	if (!name?.trim() || name.trim() === item.name) return;
	setWatchlistError(null);
	if (!user && activeWatchlist) {
	  const next = renameLocalWatchlistItem(activeWatchlist, item.id, name);
	  applyWatchlists([next], next.id);
	  return;
	}
	const finishMutation = beginWatchlistMutation();
	try {
	  const rows = await renameWatchlistItem(item.id, name);
	  applyWatchlists(rows, activeWatchlist?.id);
	} catch (err) {
	  console.error('renameWatchlistItem:', err);
	  setWatchlistError(errorMessage(err));
	} finally {
	  finishMutation();
	}
  }

  async function setWatchlistItemFlagColor(item: WatchlistViewItem, flagColor: WatchlistFlagColor | null) {
	if (item.item_type !== 'symbol') return;
	if (flagColor) {
	  setLatestWatchlistFlagColor(flagColor);
	  storeJSON(latestWatchlistFlagColorStorageKey, flagColor);
	}
	setWatchlistError(null);
	const previous = watchlists;
	setWatchlists((current) => updateActiveWatchlistItems(current, activeWatchlist?.id ?? '', (items) => items.map((candidate) => candidate.id === item.id ? { ...candidate, flag_color: flagColor } : candidate)));
	if (!user && activeWatchlist) {
	  const next = setLocalWatchlistItemFlag(activeWatchlist, item.id, flagColor);
	  applyWatchlists([next], next.id);
	  return;
	}
	const finishMutation = beginWatchlistMutation();
	try {
	  const rows = await setWatchlistItemFlag(item.id, flagColor);
	  applyWatchlists(rows, activeWatchlist?.id);
	} catch (err) {
	  console.error('setWatchlistItemFlag:', err);
	  setWatchlists(previous);
	  setWatchlistError(errorMessage(err));
	} finally {
	  finishMutation();
	}
  }

  async function handleDeleteChartAlarm(id: string) {
    const previous = alarms;
    setAlarms((current) => current.filter((alarm) => alarm.id !== id));
    try {
      await deleteAlarm(id);
    } catch (err) {
      console.error('deleteAlarm:', err);
      setAlarms(previous);
    }
  }

  async function openAlarmForSymbol(symbol: ChartSymbol, initialPrice?: number) {
    if (!user) return;
    const nextInitialPrice = Number.isFinite(initialPrice) && initialPrice && initialPrice > 0 ? initialPrice : undefined;
    setAlarmInitialPrice(nextInitialPrice);
    setDraftAlarmPrice(nextInitialPrice ?? null);
    setAlarmSymbol(symbol);
  }

  async function openAlarmForSymbolName(symbolName: string | null | undefined, initialPrice?: number) {
    if (!user) return;
    const targetSymbol = symbolName || chartSymbol;
    const resolved = findSymbol(targetSymbol);
    if (resolved) {
      await openAlarmForSymbol(resolved, initialPrice);
      return;
    }

    try {
      const found = await ensureSymbol(targetSymbol);
      if (found) {
        await openAlarmForSymbol(found, initialPrice);
        return;
      }
      setWatchlistError(`Symbol ${targetSymbol} not found`);
    } catch (err) {
      console.error('ensureSymbol:', err);
      setWatchlistError(errorMessage(err));
    }
  }

  function closeAlarmSetup() {
    setAlarmSymbol(null);
    setAlarmInitialPrice(undefined);
    setDraftAlarmPrice(null);
  }

  function addSavedAlarm(alarm: PriceAlarm) {
    setAlarms((current) => [alarm, ...current.filter((item) => item.id !== alarm.id)]);
  }

  function startReorder(itemId: string, currentOrder: WatchlistViewItem[]) {
    setWatchlistError(null);
    setWatchlistSort('manual');
    if (activeWatchlist) {
      setWatchlists((current) => updateActiveWatchlistItems(current, activeWatchlist.id, () => currentOrder));
    }
    watchlistItemsRef.current = currentOrder;
    reorderStartRef.current = watchlists;
    setDraggingItemId(itemId);
  }

  function previewReorder(activeItemId: string, targetItemId: string) {
    if (!activeWatchlist || activeItemId === targetItemId) return;
    setDragOverItemId(targetItemId);
    setWatchlists((current) => updateActiveWatchlistItems(current, activeWatchlist.id, (items) => {
      const nextItems = moveWatchlistItem(items, activeItemId, targetItemId);
      watchlistItemsRef.current = nextItems;
      return nextItems;
    }));
  }

  async function finishReorder() {
    if (!activeWatchlist || !draggingItemId) return;

    const previous = reorderStartRef.current;
    const next = watchlistItemsRef.current;
    reorderStartRef.current = null;
    setDraggingItemId(null);
    setDragOverItemId(null);
    const finishMutation = beginWatchlistMutation();
    if (!user) {
      const nextWatchlist = reorderLocalWatchlistItems(activeWatchlist, next.map((item) => item.id));
      applyWatchlists([nextWatchlist], nextWatchlist.id);
      finishMutation();
      return;
    }
    try {
      const rows = await updateWatchlistOrder(activeWatchlist.id, next.map((item) => item.id));
      applyWatchlists(rows, activeWatchlist.id);
    } catch (err) {
      console.error('updateWatchlistOrder:', err);
      if (previous) setWatchlists(previous);
      setWatchlistError(errorMessage(err));
    } finally {
      finishMutation();
    }
  }

  function cancelReorder() {
    reorderStartRef.current = null;
    setDraggingItemId(null);
    setDragOverItemId(null);
  }

  function startWatchlistReorder(watchlistId: string) {
    if (watchlists.length < 2) return;
    setWatchlistError(null);
    watchlistsOrderRef.current = watchlists;
    watchlistsOrderStartRef.current = watchlists;
    setDraggingWatchlistId(watchlistId);
  }

  function previewWatchlistReorder(activeId: string, targetId: string) {
    if (activeId === targetId) return;
    setDragOverWatchlistId(targetId);
    setWatchlists((current) => {
      const next = moveWatchlist(current, activeId, targetId);
      watchlistsOrderRef.current = next;
      return next;
    });
  }

  async function finishWatchlistReorder() {
    if (!draggingWatchlistId) return;
	if (!user) {
	  setDraggingWatchlistId(null);
	  setDragOverWatchlistId(null);
	  return;
	}

    const previous = watchlistsOrderStartRef.current;
    const next = watchlistsOrderRef.current;
    watchlistsOrderStartRef.current = null;
    setDraggingWatchlistId(null);
    setDragOverWatchlistId(null);
    const finishMutation = beginWatchlistMutation();
    try {
      const rows = await updateWatchlistsOrder(next.map((watchlist) => watchlist.id));
      applyWatchlists(rows, activeWatchlistId);
    } catch (err) {
      console.error('updateWatchlistsOrder:', err);
      if (previous) setWatchlists(previous);
      setWatchlistError(errorMessage(err));
    } finally {
      finishMutation();
    }
  }

  function cancelWatchlistReorder() {
    watchlistsOrderStartRef.current = null;
    setDraggingWatchlistId(null);
    setDragOverWatchlistId(null);
  }

  return {
    checkingSession,
    user,
    canManageWatchlists: Boolean(user),
    canUseAlarms: Boolean(user),
    canUseNineWeekInterval,
    symbols,
    ready,
    syncing,
    cloudSyncEnabled: persistence.cloudSyncEnabled,
    setCloudSyncEnabled: persistence.setCloudSyncEnabled,
    syncNow,
    signOut,
    chartRef,
    datafeed: datafeedRef.current,
    chartSymbol,
    chartSymbolData,
    chartInterval,
    layout: persistence.layout,
    settingsAdapter: persistence.settingsAdapter,
    persistLayout: persistence.persistLayout,
    flushSettings: persistence.flushSettings,
    setSelectedSymbol,
    setSelectedInterval,
    activeWatchlist,
    activeWatchlistItems,
    activeWatchlistSymbols,
    watchlistSymbolNames,
    flaggedWatchlistSymbolNames,
    flaggedWatchlistSymbolColors,
    chartAlarms,
    deleteChartAlarm: handleDeleteChartAlarm,
    addToWatchlistFromChart: handleAddToWatchlistFromChart,
    removeFromWatchlistFromChart: handleRemoveFromWatchlistFromChart,
    toggleCurrentChartSymbolInWatchlist,
    toggleCurrentChartSymbolFlag,
    setCurrentChartSymbolFlag,
    watchlists,
    setActiveWatchlistId,
    watchlistSort,
    setWatchlistSort,
    watchlistLoading,
    quotesLoading,
    hasWatchlistQuotes,
    quotes,
    watchlistError,
    importFileToWatchlist,
    createNewWatchlist,
    renameWatchlistWithName,
    toggleWatchlistPinned,
    deleteWatchlistDirect,
    removeSymbolFromWatchlist,
    removeWatchlistItem,
    renameSectionWithName,
    setWatchlistItemFlagColor,
    setAlarmSymbol,
    draggingItemId,
    dragOverItemId,
    draggingWatchlistId,
    dragOverWatchlistId,
    startReorder,
    previewReorder,
    finishReorder,
    cancelReorder,
    startWatchlistReorder,
    previewWatchlistReorder,
    finishWatchlistReorder,
    cancelWatchlistReorder,
    symbolPickerOpen,
    setSymbolPickerOpen,
    symbolPickerSymbols,
    symbolSearch,
    setSymbolSearch,
    symbolPickerCategory,
    setSymbolPickerCategory,
    symbolPickerLoading,
    requestAddSymbolToWatchlist,
    addToWatchlistSymbol,
    addingWatchlistId,
    addPendingSymbolToWatchlist,
    setAddToWatchlistSymbol,
    addSectionToWatchlist,
    addSectionAboveWatchlistItem,
    alarmSymbol,
    alarmInitialPrice,
    draftAlarmPrice,
    setDraftAlarmPrice,
    openAlarmForSymbol,
    openAlarmForSymbolName,
    closeAlarmSetup,
    addSavedAlarm,
  };
}

function getRemoteChartState(value: JsonValue | undefined): StoredChartState | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  if (!('symbol' in value) || typeof value.symbol !== 'string' || !value.symbol) return null;
  return {
    symbol: value.symbol,
    interval: 'interval' in value && typeof value.interval === 'string' && value.interval ? value.interval : '1D',
  };
}
